import { useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import type { TaskPriority } from '@/lib/types'

// Tambah task ke sebuah project — manual sepenuhnya, tanpa jalur AI.

interface Props {
  projectId: string
  onClose: () => void
}

const inputClass =
  'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white transition-all'

export function CreateTaskModal({ projectId, onClose }: Props) {
  const { createTask } = useApp()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const [fTitle, setFTitle] = useState('')
  const [fDesc, setFDesc] = useState('')
  const [fPriority, setFPriority] = useState<TaskPriority>('medium')

  const handleSave = async () => {
    if (submitting || !fTitle.trim()) return
    setSubmitting(true)
    setError('')
    try {
      await createTask(projectId, fTitle.trim(), fDesc.trim(), fPriority)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuat task')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[540px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <h2 className="font-display text-[15px] font-bold text-[#111110]">Task Baru</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Judul Task <span className="text-[#DC2626] font-normal text-xs">*wajib</span>
            </label>
            <input type="text" value={fTitle} onChange={e => setFTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave() } }}
              placeholder="Apa yang perlu dikerjakan?" className={inputClass} autoFocus />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Deskripsi Detail <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
            </label>
            <textarea value={fDesc} onChange={e => setFDesc(e.target.value)}
              placeholder="Konteks, acceptance criteria, atau langkah pengerjaan..."
              rows={4} className={`${inputClass} resize-none leading-relaxed`} />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-2">Prioritas</label>
            <div className="grid grid-cols-3 gap-2">
              {(['low','medium','high'] as TaskPriority[]).map(p => (
                <button key={p} type="button" onClick={() => setFPriority(p)}
                  className={`py-2.5 rounded-xl border-2 text-center transition-all text-sm font-medium font-body ${
                    fPriority === p
                      ? p === 'high'   ? 'border-[#DC2626] bg-[#FEF2F2] text-[#DC2626]'
                      : p === 'medium' ? 'border-[#B45309] bg-[#FEF3C7] text-[#B45309]'
                                       : 'border-[#111110] bg-[#111110] text-white'
                      : 'border-[#E0DFD8] text-[#8A8A85] hover:border-[#C8C7BF]'
                  }`}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="px-3.5 py-2.5 bg-[#FEF2F2] border border-[#FECACA] rounded-xl text-xs text-[#DC2626] font-body">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F2F1EC] flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-[#AEADA8] font-body">Status default: Not Started</p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors">
              Batal
            </button>
            <button
              onClick={handleSave}
              disabled={!fTitle.trim() || submitting}
              className="flex items-center gap-2 px-5 py-2 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Simpan Task
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
